import { FreightLaneSortOption } from '@rematter/frontend-lib-api-client';

import { FreightLaneRecord } from '~/modules/transportation/types';
import { createQueryHook } from '~/shared/providers/api';
import { parseZonedDateTime } from '~/shared/utils/dateTime';

export interface FreightLanesFilter {
  ids?: string[];
  carrierId?: string;
}

interface GetFreightLanesVariables {
  filter?: FreightLanesFilter;
  sorting?: FreightLaneSortOption[];
  pagination?: {
    offset: number;
    limit: number;
  };
}

export const useFreightLanesListQuery = createQueryHook(
  'transportation.freightLanesList',
  (variables: GetFreightLanesVariables) => ({
    freightLanes: {
      __args: {
        filter: variables.filter,
        pagination: variables.pagination,
        sorting: variables.sorting ?? ['CreatedAtDesc'],
      },
      page: {
        total: true,
      },
      entries: {
        id: true,
        freightLaneRouteId: true,
        carrierId: true,
        originLocationId: true,
        originLocationType: true,
        destinationCity: true,
        destinationState: true,
        destinationZipCode: true,
        destinationCountry: true,
        active: true,
        notes: true,
        carrier: {
          id: true,
          name: true,
        },
        freight_lane_route: {
          id: true,
          locationAId: true,
          locationAType: true,
          locationBId: true,
          locationBType: true,
          location_a: {
            id: true,
            type: true,
            facility: {
              id: true,
              facilityName: true,
            },
            accountAddress: {
              id: true,
              name: true,
              account: {
                name: true,
              },
            },
          },
          location_b: {
            id: true,
            type: true,
            facility: {
              id: true,
              facilityName: true,
            },
            accountAddress: {
              id: true,
              name: true,
              account: {
                name: true,
              },
            },
          },
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
    },
  }),
  {
    select: (data): { freightLanes: FreightLaneRecord[]; total: number } => ({
      freightLanes: (data?.freightLanes?.entries ?? []).map((entry) => ({
        id: entry.id,
        freightLaneRouteId: entry.freightLaneRouteId ?? undefined,
        carrierId: entry.carrierId,
        carrierName: entry.carrier?.name ?? '',
        transportationMode: 'TRUCK',
        originLocationId: entry.originLocationId ?? undefined,
        originLocationType: entry.originLocationType ?? undefined,
        routeLocationAId: entry.freight_lane_route?.locationAId ?? undefined,
        routeLocationBId: entry.freight_lane_route?.locationBId ?? undefined,
        routeLocationAName:
          entry.freight_lane_route?.location_a?.facility?.facilityName ??
          entry.freight_lane_route?.location_a?.accountAddress?.account?.name ??
          undefined,
        routeLocationBName:
          entry.freight_lane_route?.location_b?.facility?.facilityName ??
          entry.freight_lane_route?.location_b?.accountAddress?.account?.name ??
          undefined,
        destinationCity: entry.destinationCity ?? undefined,
        destinationState: entry.destinationState ?? undefined,
        destinationZipCode: entry.destinationZipCode ?? undefined,
        destinationCountry: entry.destinationCountry ?? undefined,
        active: entry.active,
        notes: entry.notes ?? undefined,
        timestamps: {
          createdAt: parseZonedDateTime(entry.timestamps.createdAt),
          updatedAt: parseZonedDateTime(entry.timestamps.updatedAt),
        },
      })),
      total: data?.freightLanes?.page?.total ?? 0,
    }),
  },
);
